/**
 * @file: user.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { EnvTagType } from '@euler/config';
import { HttpClient } from '@euler/lib/request';
import {
  NotificationSettingCategory,
  NotificationSettingChannel,
  NotificationSettings,
  TaskBookmark,
  TaskBookmarkSavePayload,
  UserProfile,
  UserProfileUpdateParams,
} from '@euler/model';
import { MemberPerformance } from '@euler/model/MemberPerformance';
import { UserInfo } from '@euler/model/UserInfo';
import { UserSessionProps } from '@euler/model/UserPresence';
import {
  MyCampaignRewardOrderGroup,
  MyCampaignRewardSearchParams,
} from '@euler/model/campaign/MyCampaignRewardRecord';
import { UserPresenceRef } from '@euler/model/chat';
import { CursorResult, IdType } from '@euler/model/common';
import { OrgUserWeixinBinding, Store } from '@euler/model/entity';
import { ProductReferralFeeItemSearchParams } from '@euler/model/product/ProductReferralFeeItemSearchParams';
import { ProductReferralInfo } from '@euler/model/product/ProductReferralInfo';
import type { PermissionStatus } from 'expo-tracking-transparency';
export class UserService {
  constructor(private readonly api: HttpClient) {}

  async getVisibleStores(): Promise<Store[]> {
    return await this.api.get().url('/user/authorized-stores').future();
  }

  async getUserProfileInfo(): Promise<UserProfile> {
    return await this.api.get().url('/user/profile').future();
  }

  /** The current member's own work over the last 30 days. */
  async getMyPerformance(): Promise<MemberPerformance> {
    return await this.api.get().url('/user/performance').future();
  }

  async getCurrentUserRole(): Promise<string> {
    return await this.api.get().url('/user/role').future();
  }

  async updateMobile(
    mobile: string,
    verifyCode: string,
    ticket: string,
  ): Promise<void> {
    await this.api
      .put()
      .url('/user/profile/mobile')
      .data({ mobile, verifyCode, ticket })
      .future();
  }

  async updateProfile(params: UserProfileUpdateParams): Promise<UserInfo> {
    return await this.api.patch().url('/user/profile').data(params).future();
  }

  async updateIosTrackingTransparencySetting(
    value: PermissionStatus | null,
  ): Promise<UserInfo> {
    return await this.api
      .patch()
      .url('/user/ios-tracking-transparency-setting')
      .data({ value })
      .future();
  }

  async presenceLogin(sessionProps: UserSessionProps) {
    return await this.api
      .post()
      .url('/user/presence/login')
      .data({ sessionProps })
      .future();
  }

  async presenceActive(sessionProps: UserSessionProps): Promise<boolean> {
    return await this.api
      .post()
      .url('/user/presence/active')
      .data({ sessionProps })
      .future();
  }

  async presenceLogout(uid: string) {
    return await this.api
      .post()
      .url('/user/presence/logout')
      .data({ uid })
      .future();
  }

  /**
   * App-foreground presence heartbeat — refreshes this identity's online
   * key (drive ~every 25s while foregrounded). See backend
   * docs/user-presence.md.
   */
  async presenceHeartbeat(): Promise<void> {
    await this.api.post().url('/user/presence/heartbeat').future();
  }

  /** Snapshot of which of the given identities are currently online. */
  async queryPresence(
    refs: UserPresenceRef[],
  ): Promise<{ online: UserPresenceRef[] }> {
    return await this.api
      .post()
      .url('/user/presence/query')
      .data({ refs })
      .future();
  }

  async getWeixinLoginBindStatus(): Promise<boolean> {
    return await this.api.get().url('/user/weixin-login/status').future();
  }

  async getWeixinLoginBindInfo(): Promise<OrgUserWeixinBinding | undefined> {
    return await this.api.get().url('/user/weixin-login/info').future();
  }

  async removeWeixinLoginBinding(): Promise<boolean> {
    return await this.api.delete().url('/user/weixin-login').future();
  }

  async getNotificationSettings(): Promise<NotificationSettings> {
    return await this.api.get().url('/user/notification-settings').future();
  }

  async updateNotificationSetting(
    channel: NotificationSettingChannel,
    category: NotificationSettingCategory,
    enabled: boolean,
  ): Promise<void> {
    await this.api
      .put()
      .url('/user/notification-settings')
      .data({ channel, category, enabled })
      .future();
  }

  async deleteMyAccount(): Promise<void> {
    await this.api.post().url('/user/delete-my-account').future();
  }

  async getTaskBookmarks(
    offset: number,
    limit: number,
  ): Promise<TaskBookmark[]> {
    return await this.api
      .get()
      .url('/user/task-bookmarks', { offset, limit })
      .future();
  }

  async addTaskBookmark(
    payload: TaskBookmarkSavePayload,
  ): Promise<TaskBookmark> {
    return await this.api
      .post()
      .url('/user/task-bookmarks')
      .data({ payload })
      .future();
  }

  async deleteTaskBookmarkByTaskNo(taskNo: string) {
    return await this.api
      .delete()
      .url('/user/task-bookmarks/:taskNo', { taskNo })
      .future();
  }

  async doesTaskBookmarkExist(taskNo: string): Promise<boolean> {
    return await this.api
      .get()
      .url('/user/task-bookmarks/:taskNo/exists', { taskNo })
      .future();
  }

  async getMiniprogramDashboardLaunchParams(params?: {
    envTag?: EnvTagType;
  }): Promise<{
    appId: string;
    userName: string;
    pagePath: string;
  }> {
    return await this.api
      .get()
      .url('/user/miniprogram/launch-params/dashboard', params)
      .future();
  }

  async qrcodeAuthChallengeScanned(
    code: string,
  ): Promise<{ deviceName: string }> {
    return await this.api
      .post()
      .url('/user/qrcode-auth/challenge/scanned')
      .data({ code })
      .future();
  }

  async qrcodeAuthChallengeConfirmed(code: string): Promise<void> {
    await this.api
      .post()
      .url('/user/qrcode-auth/challenge/confirmed')
      .data({ code })
      .future();
  }

  async qrcodeAuthChallengeCanceled(code: string): Promise<void> {
    await this.api
      .post()
      .url('/user/qrcode-auth/challenge/canceled')
      .data({ code })
      .future();
  }

  async searchMyProductReferralFeeItems(
    params: ProductReferralFeeItemSearchParams,
    nextKey: IdType | undefined,
    limit: number,
  ): Promise<CursorResult<ProductReferralInfo, IdType>> {
    return await this.api
      .post()
      .url('/members/fee-items/search')
      .data({ params, nextKey, limit })
      .future();
  }

  async searchMyCampaignRewards(
    params: MyCampaignRewardSearchParams,
    nextKey: string | undefined,
    limit: number,
  ): Promise<CursorResult<MyCampaignRewardOrderGroup, string>> {
    return await this.api
      .post()
      .url('/members/campaign-rewards/search')
      .data({ params, nextKey, limit })
      .future();
  }
}
